import React, { Suspense, lazy } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const VerifyEmail = lazy(() => import("@/pages/VerifyEmail"));
const EmailVerificationRequired = lazy(() => import("@/pages/EmailVerificationRequired"));

// Code-split the heavier app + legal pages so the initial bundle stays small.
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const CheckIn = lazy(() => import("@/pages/CheckIn"));
const Coach = lazy(() => import("@/pages/Coach"));
const UnloadRoom = lazy(() => import("@/pages/UnloadRoom"));
const FirstWorkout = lazy(() => import("@/pages/FirstWorkout"));
const MaskOff = lazy(() => import("@/pages/MaskOff"));
const BeforeReact = lazy(() => import("@/pages/BeforeReact"));
const FrustrationReset = lazy(() => import("@/pages/FrustrationReset"));
const BrotherCheck = lazy(() => import("@/pages/BrotherCheck"));
const Community = lazy(() => import("@/pages/Community"));
const WordWisdom = lazy(() => import("@/pages/WordWisdom"));
const BibleSearch = lazy(() => import("@/pages/BibleSearch"));
const Challenges = lazy(() => import("@/pages/Challenges"));
const Progress = lazy(() => import("@/pages/Progress"));
const Emergency = lazy(() => import("@/pages/Emergency"));
const Settings = lazy(() => import("@/pages/Settings"));
const Subscribe = lazy(() => import("@/pages/Subscribe"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const Terms = lazy(() => import("@/pages/Terms"));
const AccountDeletion = lazy(() => import("@/pages/AccountDeletion"));
const LegacyTracker = lazy(() => import("@/pages/LegacyTracker"));
const GrowthPlan = lazy(() => import("@/pages/GrowthPlan"));
const Groups = lazy(() => import("@/pages/Groups"));
const Moderation = lazy(() => import("@/pages/Moderation"));
const BetaTesters = lazy(() => import("@/pages/BetaTesters"));

function Splash() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="heading text-5xl font-black tracking-tighter text-primary animate-pulse">UNLOADDAILY</div>
    </div>
  );
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/app/login" replace />;
  return children;
}

// Blocks sensitive features until the user's email is verified.
// Grandfathers existing users whose account has no email_verified flag.
function VerifiedGate({ children }) {
  const { user } = useAuth();
  if (user && user.email_verified === false) return <Navigate to="/app/email-verification-required" replace />;
  return children;
}

export default function App() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  const authedHome = user && user.email_verified === false ? "/app/email-verification-required" : "/app";
  // After signup, a not-yet-completed First Workout takes priority (it routes onward to verify/dashboard
  // itself). This does NOT auto-launch on normal login — only when landing on the register route.
  const fwStatus = user?.first_workout?.status;
  const registerHome = user && fwStatus && fwStatus !== "completed" ? "/app/first-workout" : authedHome;
  return (
    <Suspense fallback={<Splash />}>
      <Routes>
        {/* Public */}
        <Route path="/" element={<Landing />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/account-deletion" element={<AccountDeletion />} />

        {/* Auth */}
        <Route path="/app/login" element={user ? <Navigate to={authedHome} replace /> : <Login />} />
        <Route path="/app/register" element={user ? <Navigate to={registerHome} replace /> : <Register />} />
        <Route path="/app/forgot-password" element={user ? <Navigate to={authedHome} replace /> : <ForgotPassword />} />
        <Route path="/app/reset-password" element={<ResetPassword />} />
        <Route path="/app/verify-email" element={<VerifyEmail />} />

        {/* First Workout — full-screen, distraction-free (no app chrome) */}
        <Route
          path="/app/first-workout"
          element={
            <Protected>
              <FirstWorkout />
            </Protected>
          }
        />

        {/* App */}
        <Route
          path="/app"
          element={
            <Protected>
              <Layout />
            </Protected>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="email-verification-required" element={<EmailVerificationRequired />} />
          <Route path="checkin" element={<CheckIn />} />
          <Route path="coach" element={<VerifiedGate><Coach /></VerifiedGate>} />
          <Route path="unload" element={<VerifiedGate><UnloadRoom /></VerifiedGate>} />
          <Route path="maskoff" element={<MaskOff />} />
          <Route path="before-react" element={<BeforeReact />} />
          <Route path="reset" element={<FrustrationReset />} />
          <Route path="brothers" element={<BrotherCheck />} />
          <Route path="community" element={<VerifiedGate><Community /></VerifiedGate>} />
          <Route path="word" element={<WordWisdom />} />
          <Route path="bible" element={<BibleSearch />} />
          <Route path="challenges" element={<Challenges />} />
          <Route path="progress" element={<Progress />} />
          <Route path="emergency" element={<Emergency />} />
          <Route path="settings" element={<Settings />} />
          <Route path="subscribe" element={<VerifiedGate><Subscribe /></VerifiedGate>} />
          <Route path="legacy" element={<LegacyTracker />} />
          <Route path="growth-plan" element={<GrowthPlan />} />
          <Route path="groups" element={<VerifiedGate><Groups /></VerifiedGate>} />
          <Route path="moderation" element={<Moderation />} />
          <Route path="beta" element={<BetaTesters />} />
        </Route>

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
  );
}
